import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { DataTable, type DataTableColumn, type DataTableSort } from '@/components/data-table/DataTable'
import { Pagination } from '@/components/data-table/Pagination'
import { DetailOverlay } from '@/components/overlay/DetailOverlay'
import { MarketplaceBadgeList } from '@/components/warehouse/primitives'
import { StorefrontCompactCell } from '@/components/storefronts/StorefrontCompactCell'
import { ProductQuickPreview } from '@/components/warehouse/ProductQuickPreview'
import { WarehouseGroupedTable, WarehouseLeafTable } from '@/components/warehouse/WarehouseGroupedTable'
import { WarehouseFilterChips, WarehouseFilters, warehouseFilterChips } from '@/components/warehouse/WarehouseFilters'
import { WarehouseSelectionBar } from '@/components/warehouse/WarehouseSelectionBar'
import { SelectAllCheckbox } from '@/components/warehouse/SelectAllCheckbox'
import { CardSettingsDialog, type CardSettingsSection } from '@/components/storefronts/CardSettingsDialog'
import { lifecycleLabels, WarehouseToolbar } from '@/components/warehouse/WarehouseToolbar'
import { getInventory, getInventoryGroups, getInventoryVariant, type InventoryFilters, type InventoryGroupFilters } from '@/lib/api/client/inventory'
import { fetchOrderById } from '@/lib/api/client/orders'
import type { InventoryGroupPage, InventoryGroupRow, InventoryListItem, InventoryPage, InventoryRow, InventoryVariant } from '@/lib/api/schemas/inventory.schema'
import type { YmEconomicsStats } from '@/lib/api/schemas/ymarket-pricing.schema'
import { displayArticle, marketplaceBadges, productStatusOf } from '@/lib/warehouse/projection'
import { SORT_COLUMNS, activeFilterCount, changeGrouping, filterParamNames, parkedFilterLabels, parseWarehouseUrl, requestGroupFilters, resetFilters, serializeWarehouseUrl, switchPresetFilters, togglePreset, type WarehouseSortColumn, type WarehouseUrlState } from '@/lib/warehouse/url-state'
import { useWarehouseSelection, type WarehouseSelection } from '@/hooks/useWarehouseSelection'
import { INVENTORY_COLUMN_REGISTRY, WAREHOUSE_DEFAULT_VISIBLE_COLUMNS, type InventoryColumnId } from '@/lib/views/columns/inventory'
import { PresetSwitcher } from '@/components/views/PresetSwitcher'
import { useViewPresets } from '@/components/views/useViewPresets'
import { OrderDetailDrawer } from '@/components/orders/OrderDetailDrawer'
import { OrderDetailContent } from '@/components/orders/OrderDetailContent'
import { MarketEconomicsSection } from '@/components/warehouse/MarketEconomicsSection'
import { inventoryIncludeForColumns, inventoryPreset } from '@/lib/views/warehouse-presets'
import type { ViewHeaderButton, ViewPresetKey } from '@/lib/views/presets'
import { SELECTION_ACTION_REGISTRY, type InventorySelectionAction, type SelectionEligibility } from '@/lib/views/actions'
import { collectYmPrices } from '@/lib/api/client/ymarket-pricing'
import { announceOperation } from '@/hooks/useOperations'
import { warehouseRequestPlan, type WarehouseRequest } from '@/app/inventory/request-plan'
import { useWarehousePanelCounts } from '@/hooks/useWarehousePanelCounts'
import { resolveEligibleSelection } from '@/lib/warehouse/selection-eligibility'

type ProductColumn = InventoryColumnId
type ProductTableColumn = ProductColumn | 'selection'

type RussianNounForms = readonly [one: string, few: string, many: string]

const PRODUCT_NOUN_FORMS: RussianNounForms = ['товар', 'товара', 'товаров']
const ORDER_NOUN_FORMS: RussianNounForms = ['заказ', 'заказа', 'заказов']
const GROUP_NOUN_FORMS: RussianNounForms = ['группа', 'группы', 'групп']
const SOURCE_RECORD_NOUN_FORMS: RussianNounForms = ['запись источника', 'записи источника', 'записей источника']

function russianPlural(value: number, forms: RussianNounForms): string {
  const remainder100 = Math.abs(value) % 100
  const remainder10 = remainder100 % 10
  if (remainder100 >= 11 && remainder100 <= 19) return forms[2]
  if (remainder10 === 1) return forms[0]
  if (remainder10 >= 2 && remainder10 <= 4) return forms[1]
  return forms[2]
}

function productRowLabel(variant: InventoryListItem): string {
  const article = displayArticle(variant) || 'Без артикула'
  const title = variant.title ?? 'Название не записано'
  return `Открыть товар: ${title} · ${article}${variant.techSize ? ` · ${variant.techSize}` : ''}`
}

function uniqueProducts(items: readonly InventoryListItem[]): InventoryListItem[] {
  const seen = new Set<string>()
  return items.filter((item) => {
    if (item.rowGrain !== 'piece') return false
    if (seen.has(item.productId)) return false
    seen.add(item.productId)
    return true
  })
}

function productSortColumnOf(column: WarehouseSortColumn, visibleColumns: readonly InventoryColumnId[]): ProductColumn {
  if (column === 'article' && visibleColumns.includes('product') && !visibleColumns.includes('article')) return 'product'
  if (column === 'lifecycle') return 'article'
  switch (column) {
    case 'uin':
    case 'article':
    case 'title':
    case 'product':
    case 'weight':
    case 'activity':
    case 'size':
    case 'photoCount':
    case 'onecPrice':
    case 'wildberriesPrice':
    case 'ymarketPrice':
    case 'ozonPrice':
    case 'oriSitePrice':
    case 'ymarketSellsFor':
    case 'ymarketPromo':
    case 'ymarketBoost':
    case 'ymarketRubPerGram':
    case 'ymarketPurchaseCost':
    case 'ymarketEarnings':
    case 'ymarketEarningsPercent':
    case 'ymarketNetPerGram':
    case 'ymarketShowcase':
    case 'ymarketShowcasePerGram':
    case 'ymarketShowcaseThresholdRatio':
      return column
    default:
      return 'article'
  }
}

function warehouseSortColumnOf(column: ProductColumn): WarehouseSortColumn {
  switch (column) {
    case 'uin':
    case 'article':
    case 'title':
    case 'product':
    case 'weight':
    case 'activity':
    case 'size':
    case 'photoCount':
    case 'onecPrice':
    case 'wildberriesPrice':
    case 'ymarketPrice':
    case 'ozonPrice':
    case 'oriSitePrice':
    case 'ymarketSellsFor':
    case 'ymarketPromo':
    case 'ymarketBoost':
    case 'ymarketRubPerGram':
    case 'ymarketPurchaseCost':
    case 'ymarketEarnings':
    case 'ymarketEarningsPercent':
    case 'ymarketNetPerGram':
    case 'ymarketShowcase':
    case 'ymarketShowcasePerGram':
    case 'ymarketShowcaseThresholdRatio':
      return column
    default:
      return 'article'
  }
}

function warehouseSortOf(column: string, direction: 'ascending' | 'descending'): WarehouseUrlState['sort'] {
  switch (column) {
    case 'uin':
    case 'article':
    case 'title':
    case 'product':
    case 'weight':
    case 'lifecycle':
    case 'activity':
    case 'size':
    case 'photoCount':
    case 'onecPrice':
    case 'wildberriesPrice':
    case 'ymarketPrice':
    case 'ozonPrice':
    case 'oriSitePrice':
    case 'ymarketSellsFor':
    case 'ymarketPromo':
    case 'ymarketBoost':
    case 'ymarketRubPerGram':
    case 'ymarketPurchaseCost':
    case 'ymarketEarnings':
    case 'ymarketEarningsPercent':
    case 'ymarketNetPerGram':
    case 'ymarketShowcase':
    case 'ymarketShowcasePerGram':
    case 'ymarketShowcaseThresholdRatio':
    case 'orderNumber':
    case 'orderCreatedAt':
    case 'buyer':
    case 'orderStatus':
    case 'city':
    case 'orderAmount':
      return { column, direction }
    default:
      return { column: 'article', direction }
  }
}

function inventorySortOf(sort: WarehouseUrlState['sort']): DataTableSort<ProductColumn> | null {
  switch (sort.column) {
    case 'uin':
    case 'article':
    case 'title':
    case 'product':
    case 'weight':
    case 'activity':
    case 'size':
    case 'photoCount':
    case 'onecPrice':
    case 'wildberriesPrice':
    case 'ymarketPrice':
    case 'ozonPrice':
    case 'oriSitePrice':
    case 'ymarketSellsFor':
    case 'ymarketPromo':
    case 'ymarketBoost':
    case 'ymarketRubPerGram':
    case 'ymarketPurchaseCost':
    case 'ymarketEarnings':
    case 'ymarketEarningsPercent':
    case 'ymarketNetPerGram':
    case 'ymarketShowcase':
    case 'ymarketShowcasePerGram':
    case 'ymarketShowcaseThresholdRatio':
      return { column: sort.column, direction: sort.direction }
    default:
      return null
  }
}

const IN_STOCK_REFUSAL = 'Не на складе — заливать нельзя'

function inStockEligibility(row: InventoryListItem): SelectionEligibility {
  return productStatusOf(row).status === 'in_stock' ? { ok: true } : { ok: false, reason: IN_STOCK_REFUSAL }
}

/** A row is selectable when any of the preset's actions accepts it; the first refusal explains why not. */
function actionsEligibility(actions: readonly InventorySelectionAction[]): (row: InventoryListItem) => SelectionEligibility {
  if (actions.length === 0) return inStockEligibility
  return (row) => {
    const verdicts = actions.map((action) => action.eligible(row))
    return verdicts.find((verdict) => verdict.ok) ?? verdicts[0] ?? inStockEligibility(row)
  }
}

const presetDefaults = (view: ViewPresetKey): InventoryFilters => inventoryPreset(view).filters

const HEADER_BUTTON_LABELS: Record<ViewHeaderButton, string> = {
  cardSettings: 'Настройки карточек',
  ymarketCollect: 'Собрать цены сейчас',
}

const HEADER_BUTTON_HINTS: Record<ViewHeaderButton, string> = {
  cardSettings: 'Пол и цель ₽/г, упаковка для тарифов и что можно заливать на каждую площадку.',
  ymarketCollect: 'Прочитать с Яндекс Маркета цены, акции, ставки буста и цену на витрине. На Маркете ничего не меняется; сбор идёт в фоне и виден в «Операциях».',
}

export function ProductTable({
  rows,
  inventory,
  loading,
  error,
  sort,
  onSortChange,
  onActivate,
  selectedVariantId,
  visibleColumns = WAREHOUSE_DEFAULT_VISIBLE_COLUMNS,
  selection,
  outlierIds,
  eligibility = inStockEligibility,
  onSelectAll,
}: {
  rows: readonly InventoryListItem[]
  inventory: InventoryPage | null
  loading: boolean
  error: string | null
  sort: WarehouseUrlState['sort']
  onSortChange: (sort: WarehouseUrlState['sort']) => void
  onActivate: (variant: InventoryListItem) => void
  selectedVariantId: string | null
  visibleColumns?: readonly InventoryColumnId[]
  selection?: WarehouseSelection
  outlierIds?: ReadonlySet<string>
  eligibility?: (row: InventoryListItem) => SelectionEligibility
  /** Selects every piece of the current filter across pages; the header checkbox calls it. */
  onSelectAll?: () => Promise<void>
}) {
  const productVisibleColumns = visibleColumns.filter((id) => {
    const column = INVENTORY_COLUMN_REGISTRY.find((candidate) => candidate.id === id)
    return column !== undefined && !column.facets.includes('order')
  })
  const columnsForTable = productVisibleColumns.length > 0 ? productVisibleColumns : WAREHOUSE_DEFAULT_VISIBLE_COLUMNS
  const selectableIds = useMemo(
    () => rows.filter((row) => eligibility(row).ok).map((row) => row.productId),
    [eligibility, rows],
  )
  const selectionColumn: readonly DataTableColumn<InventoryListItem, ProductTableColumn>[] = selection ? [{
    id: 'selection',
    label: 'Выбор',
    header: <SelectAllCheckbox selection={selection} total={inventory?.total ?? 0} onSelectAll={onSelectAll} />,
    render: (row: InventoryListItem) => {
      const eligible = eligibility(row)
      return <input type="checkbox" data-product-id={row.productId} checked={eligible.ok && selection.isSelected(row.productId)} disabled={!eligible.ok} title={eligible.ok ? undefined : eligible.reason} onChange={(event) => { const range = event.nativeEvent instanceof MouseEvent && event.nativeEvent.shiftKey ? selectableIds : undefined; selection.toggle(row.productId, range) }} aria-label={`Выбрать ${productRowLabel(row)}`} />
    },
  }] : []
  const columns = useMemo<readonly DataTableColumn<InventoryListItem, ProductTableColumn>[]>(() => [
    ...selectionColumn,
    ...columnsForTable.flatMap((columnId) => {
      const column = INVENTORY_COLUMN_REGISTRY.find((candidate) => candidate.id === columnId)
      if (column === undefined) return []
      return [{
        id: column.id,
        label: column.label,
        ...(column.serverSortKey !== undefined ? { serverSortable: true } : {}),
        render: (row: InventoryListItem) => column.id === 'marketplaces'
          ? <MarketplaceBadgeList badges={marketplaceBadges(row, inventory?.providerSlots ?? null)} compact />
          : column.id === 'storefronts'
            ? <StorefrontCompactCell row={row} providerSlots={inventory?.providerSlots ?? null} />
            : column.cell(row),
      }]
    }),
  ], [columnsForTable, inventory?.providerSlots, inventory?.total, onSelectAll, selectableIds, selection])

  const productSortColumn = productSortColumnOf(sort.column, columnsForTable)

  return <DataTable<InventoryListItem, ProductTableColumn>
    caption="Товары на складе"
    columns={columns}
    rows={rows}
    rowKey={(row) => row.productId}
    rowLabel={productRowLabel}
    onRowActivate={(row, event) => {
      // Shift+click extends the selection range, as it does on the checkbox; a plain click opens the preview.
      if (event?.shiftKey && selection && eligibility(row).ok) {
        selection.toggle(row.productId, selectableIds)
        return
      }
      onActivate(row)
    }}
    selectedRowKey={rows.find((row) => row.productId === selectedVariantId)?.productId ?? null}
    selectedRowClassName="bg-gold/10"
    rowClassName={(row) => outlierIds?.has(row.productId) ? 'bg-st-unpaid/10' : undefined}
    loading={loading}
    error={error}
    emptyMessage="По заданным условиям товаров нет."
    loadingMessage="Загружаем товары…"
    sort={{ column: productSortColumn, direction: sort.direction }}
    onSortChange={(next) => { if (next.column !== 'selection') onSortChange({ column: warehouseSortColumnOf(next.column), direction: next.direction }) }}
  />
}

export function WarehousePage({ refreshToken = 0 }: { refreshToken?: number } = {}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const state = useMemo(() => parseWarehouseUrl(searchParams), [searchParams])
  const presetKey: ViewPresetKey = state.view ?? 'actual'
  const builtinPreset = inventoryPreset(presetKey)
  const presetFilterParams = useMemo(() => filterParamNames(builtinPreset.filters), [builtinPreset.filters])
  const saved = useViewPresets()
  const defaultFiltersApplied = useRef(presetFilterParams.some((name) => searchParams.has(name)))
  const visibleColumns = saved.overrides[presetKey]?.columns ?? builtinPreset.columns
  const presetSort = saved.overrides[presetKey]?.sort ?? builtinPreset.sort
  const presetGrouping = presetKey === 'orders' ? 'order' : 'none'
  const selectedSort = !searchParams.has('sort') && state.grouping === presetGrouping ? presetSort : state.sort
  const viewSort = warehouseSortOf(selectedSort.column, selectedSort.direction)
  const presetFiltersApplied = presetKey === 'orders' || presetFilterParams.some((name) => searchParams.has(name))
  const viewFilters = presetFiltersApplied || defaultFiltersApplied.current
    ? state.filters
    : { ...state.filters, ...builtinPreset.filters }
  const include = inventoryIncludeForColumns(visibleColumns)
  const viewState = useMemo(() => ({ ...state, filters: viewFilters, sort: viewSort, include }), [include, state, viewFilters, viewSort])
  const [groups, setGroups] = useState<InventoryGroupPage | null>(null)
  const [inventory, setInventory] = useState<InventoryPage | null>(null)
  const [selectionStats, setSelectionStats] = useState<YmEconomicsStats | null>(null)
  const outlierIds = useMemo(() => new Set(selectionStats?.outlierIds ?? []), [selectionStats])
  const [selected, setSelected] = useState<InventoryVariant | null>(null)
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null)
  const [selectedOrder, setSelectedOrder] = useState<Awaited<ReturnType<typeof fetchOrderById>>>(null)
  const [orderLoading, setOrderLoading] = useState(false)
  const [orderError, setOrderError] = useState<string | null>(null)
  const [selectedPopulationRowKey, setSelectedPopulationRowKey] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const filterTriggerRef = useRef<HTMLButtonElement | null>(null)
  const pushedPreview = useRef(false)
  const reconciliationMode = (viewFilters.rowGrain !== undefined && viewFilters.rowGrain !== 'piece') || viewFilters.unresolvedOnly === true
  // The whole visible filter travels, «Статус» included, so expectedTotal is the table total.
  const selectionFilters = useMemo<InventoryFilters>(() => ({
    ...viewFilters,
    ...(state.lifecycle === 'all' ? {} : { lifecycle: state.lifecycle }),
    ...(state.withPhotos ? { withPhotos: true } : {}),
  }), [state.lifecycle, state.withPhotos, viewFilters])
  const selectionFilterKey = useMemo(() => JSON.stringify({ filters: viewFilters, lifecycle: state.lifecycle, withPhotos: state.withPhotos, grouping: state.grouping }), [state.grouping, state.lifecycle, state.withPhotos, viewFilters])
  const selection = useWarehouseSelection(selectionFilterKey)
  const currentSelectionKey = useRef(selectionFilterKey)
  currentSelectionKey.current = selectionFilterKey
  const [cardSettingsSection, setCardSettingsSection] = useState<CardSettingsSection | null>(null)
  const [collecting, setCollecting] = useState(false)
  const [collectError, setCollectError] = useState<string | null>(null)
  const [actionRefreshToken, setActionRefreshToken] = useState(0)
  const parkedFilters = useMemo(() => state.parked ?? {}, [state.parked])
  const [selectionBarHeight, setSelectionBarHeight] = useState(0)

  const write = useCallback((next: Partial<WarehouseUrlState>, options: { push?: boolean } = {}) => {
    setSearchParams(serializeWarehouseUrl({ ...state, ...next }, presetDefaults), { replace: !options.push })
  }, [setSearchParams, state])

  useEffect(() => {
    const canonical = serializeWarehouseUrl(viewState, presetDefaults)
    if (searchParams.toString() !== canonical.toString()) {
      setSearchParams(canonical, { replace: true })
    }
    if (presetFiltersApplied) defaultFiltersApplied.current = true
  }, [presetFiltersApplied, searchParams, setSearchParams, viewState])

  const groupQuery = useMemo(() => requestGroupFilters(viewState), [viewState])
  const effectiveRefreshToken = refreshToken + actionRefreshToken
  const requestPlan = useMemo(() => warehouseRequestPlan(viewState, effectiveRefreshToken), [effectiveRefreshToken, viewState])
  const signature = useMemo(() => JSON.stringify(requestPlan), [requestPlan])
  const facetCache = useRef(new Map<string, InventoryPage>())
  const facetInFlight = useRef(new Map<string, Promise<InventoryPage>>())

  useEffect(() => {
    setSelectedPopulationRowKey(null)
  }, [signature])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    const loadRequest = async (request: WarehouseRequest): Promise<
      | { kind: 'inventory'; value: InventoryPage }
      | { kind: 'groups'; value: InventoryGroupPage }
    > => {
      if (request.kind === 'groups') {
        return { kind: 'groups', value: await getInventoryGroups(request.level, request.filters, controller.signal) }
      }
      if (request.purpose === 'facets') {
        const cached = facetCache.current.get(request.signature)
        if (cached !== undefined) return { kind: 'inventory', value: cached }
        const pending = facetInFlight.current.get(request.signature)
        if (pending !== undefined) return { kind: 'inventory', value: await pending }
        const loaded = getInventory(request.filters)
          .then((value) => {
            facetCache.current.set(request.signature, value)
            return value
          })
          .finally(() => facetInFlight.current.delete(request.signature))
        facetInFlight.current.set(request.signature, loaded)
        return { kind: 'inventory', value: await loaded }
      }
      return { kind: 'inventory', value: await getInventory(request.filters, controller.signal) }
    }

    Promise.all(requestPlan.map(loadRequest))
      .then((results) => {
        let nextInventory: InventoryPage | null = null
        let nextGroups: InventoryGroupPage | null = null
        for (const result of results) {
          if (result.kind === 'inventory') nextInventory = result.value
          else nextGroups = result.value
        }
        if (!controller.signal.aborted) {
          setInventory(nextInventory)
          setGroups(nextGroups)
        }
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return
        // A count or a page of the previous read is not a statement about this one.
        setInventory(null)
        setGroups(null)
        setError(reason instanceof Error ? reason.message : 'Не удалось загрузить склад')
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  // Request identity is the serialized value, not either object's identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])

  useEffect(() => {
    if (!state.selectedVariantId) {
      setSelected(null)
      return
    }
    const controller = new AbortController()
    getInventoryVariant(state.selectedVariantId, controller.signal)
      .then((value) => { if (!controller.signal.aborted) setSelected(value) })
      .catch(() => { if (!controller.signal.aborted) setSelected(null) })
    return () => controller.abort()
  }, [state.selectedVariantId])

  useEffect(() => {
    if (selectedPopulationRowKey === null) return
    const visibleRows = state.grouping === 'none' || reconciliationMode
      ? (inventory?.rows ?? [])
      : (groups?.populationRows ?? [])
    const visible = visibleRows.some((row) => row.rowKey === selectedPopulationRowKey)
    if (!visible) setSelectedPopulationRowKey(null)
  }, [groups?.populationRows, inventory?.rows, reconciliationMode, selectedPopulationRowKey, state.grouping])

  const products = useMemo(() => uniqueProducts(inventory?.items ?? []), [inventory?.items])
  // Rows seen while this filter's selection lives, so an action knows pieces picked on other pages.
  const seenRows = useRef(new Map<string, InventoryListItem>())
  useEffect(() => {
    seenRows.current = new Map()
  }, [selectionFilterKey])
  useEffect(() => {
    for (const row of products) seenRows.current.set(row.productId, row)
  }, [products])
  const selectedEconomics = selected === null ? null : products.find((row) => row.productId === selected.productId)?.ymarketEconomics ?? selected.ymarketEconomics
  const selectedRows = useMemo(
    () => [...selection.selectedIds].flatMap((id) => { const row = seenRows.current.get(id); return row === undefined ? [] : [row] }),
    [selection.selectedIds, products],
  )
  // The grouped endpoint pages groups, while the companion inventory request
  // pages individual products. Its rows therefore cannot safely answer
  // "all on this page" when a grouping mode is active; grouped children expose
  // their own checkboxes as they are opened.
  const populationRows = useMemo(() => (inventory?.rows ?? []).filter((row) => row.rowGrain !== 'piece'), [inventory?.rows])
  const groupingFilters = useMemo<InventoryGroupFilters>(() => ({ ...groupQuery, limit: undefined, offset: undefined }), [groupQuery])
  const total = reconciliationMode
    ? inventory?.rowPopulationTotal ?? 0
    : state.grouping === 'none'
      ? inventory?.total ?? 0
      : groups?.total ?? 0
  const totalNoun = russianPlural(total, reconciliationMode
    ? SOURCE_RECORD_NOUN_FORMS
    : state.grouping === 'none'
      ? PRODUCT_NOUN_FORMS
      : state.grouping === 'order'
        ? ORDER_NOUN_FORMS
        : GROUP_NOUN_FORMS)
  const totalKnown = (reconciliationMode || state.grouping === 'none' ? inventory : groups) !== null
  const totalDescription = error !== null
    ? 'Количество не загружено'
    : !totalKnown
      ? 'считаем…'
    : reconciliationMode
      ? `${total.toLocaleString('ru-RU')} ${totalNoun} · изделия не определены`
      : `${total.toLocaleString('ru-RU')} ${totalNoun}`
  const activeFilters = activeFilterCount(viewFilters) > 0 || state.withPhotos || state.lifecycle !== 'all'
  const showResultCountLabel = activeFilters || presetKey === 'orders'
  const filterChipCount = warehouseFilterChips(viewFilters, state.lifecycle).length
  const parkedLabels = presetKey === 'orders' ? parkedFilterLabels(parkedFilters) : []
  const parkedNote = parkedLabels.length > 0
    ? `В «Заказах» не действуют: ${parkedLabels.join(', ')}. Они вернутся при переходе на «Актуальное» или «Площадки».`
    : null
  const panelCounts = useWarehousePanelCounts({
    open: filtersOpen && !reconciliationMode,
    view: presetKey,
    filters: viewFilters,
    base: { lifecycle: state.lifecycle, withPhotos: state.withPhotos || undefined },
    providerSlots: inventory?.providerSlots ?? null,
    refreshToken: effectiveRefreshToken,
  })
  const selectionActions = useMemo<readonly InventorySelectionAction[]>(
    () => builtinPreset.actions
      .map((key) => SELECTION_ACTION_REGISTRY.find((action) => action.key === key))
      .filter((action): action is InventorySelectionAction => action !== undefined),
    [builtinPreset.actions],
  )
  const rowEligibility = useMemo(() => actionsEligibility(selectionActions), [selectionActions])
  const selectAllEligible = useCallback(async () => {
    const key = selectionFilterKey
    const total = inventory?.total ?? 0
    const resolved = await resolveEligibleSelection({
      total,
      eligibility: rowEligibility,
      readPage: (offset, limit) => getInventory({ ...selectionFilters, include: include.length > 0 ? include : undefined, limit, offset }),
    }).catch(() => ({ kind: 'filter' as const }))
    // A filter changed while the population was read: that selection belongs to the old filter.
    if (currentSelectionKey.current !== key) return
    if (resolved.kind === 'eligible') selection.selectEligible(resolved.ids, resolved.refused)
    else selection.selectAllMatching(selectionFilters, total)
  }, [include, inventory?.total, rowEligibility, selection, selectionFilterKey, selectionFilters])
  const headerButtons = reconciliationMode ? [] : (builtinPreset.headerButtons ?? [])

  const collectPrices = async () => {
    setCollecting(true)
    setCollectError(null)
    try {
      announceOperation(await collectYmPrices())
    } catch (reason: unknown) {
      setCollectError(reason instanceof Error && reason.message !== '' ? reason.message : 'Не удалось запустить сбор цен Маркета.')
    } finally {
      setCollecting(false)
    }
  }

  const pressHeaderButton = (button: ViewHeaderButton) => {
    if (button === 'cardSettings') setCardSettingsSection('cards')
    else void collectPrices()
  }

  const openPreview = (variant: InventoryListItem) => {
    const alreadyOpen = state.selectedVariantId !== null
    if (!alreadyOpen) pushedPreview.current = true
    setSelectedPopulationRowKey(null)
    write({ selectedVariantId: variant.productId }, { push: !alreadyOpen })
  }
  const openPopulationRow = (row: InventoryRow) => {
    setSelectedPopulationRowKey(row.rowKey)
  }
  const closePreview = () => {
    if (pushedPreview.current) {
      pushedPreview.current = false
      navigate(-1)
      return
    }
    write({ selectedVariantId: null })
  }
  const changeSort = (column: WarehouseSortColumn, direction: 'ascending' | 'descending') => {
    if (SORT_COLUMNS.includes(column)) {
      const nextSort = { column, direction }
      write({ sort: nextSort, page: 1 })
      const presetSort = inventorySortOf(nextSort)
      if (presetSort) void saved.save(presetKey, { columns: visibleColumns, sort: presetSort })
    }
  }

  const inStockSampleProductId = useMemo(
    () => products.find((product) => productStatusOf(product).status === 'in_stock')?.productId ?? products[0]?.productId ?? null,
    [products],
  )

  const changePreset = (nextKey: ViewPresetKey) => {
    const next = inventoryPreset(nextKey)
    defaultFiltersApplied.current = true
    // A piece in an order has left the shelf, so «Заказы» takes no stock scope and
    // no warehouse-only filter; those are set aside and return on leaving it.
    const switched = switchPresetFilters(state.filters, nextKey, next.filters, parkedFilters)
    write({ view: nextKey, filters: switched.filters, parked: switched.parked, sort: warehouseSortOf(next.sort.column, next.sort.direction), include: inventoryIncludeForColumns(next.columns), grouping: nextKey === 'orders' ? 'order' : 'none', selectedOrderId: nextKey === 'orders' ? state.selectedOrderId : null, page: 1 })
  }

  const openStockMismatches = useCallback(() => {
    defaultFiltersApplied.current = true
    write({
      view: 'actual',
      filters: { stockMismatch: true },
      lifecycle: 'all',
      grouping: 'none',
      withPhotos: false,
      selectedVariantId: null,
      selectedOrderId: null,
      page: 1,
    })
  }, [write])

  const openOrderById = useCallback((orderId: number) => {
    setSelectedOrderId(Number.isSafeInteger(orderId) && orderId > 0 ? orderId : null)
    setSelectedOrder(null)
    setOrderError(null)
    if (!Number.isSafeInteger(orderId) || orderId <= 0) {
      setOrderLoading(false)
      setOrderError('Идентификатор заказа недоступен.')
      return
    }
    setOrderLoading(true)
    void fetchOrderById(orderId)
      .then((order) => {
        if (order === null) throw new Error('Заказ не найден')
        setSelectedOrder(order)
      })
      .catch((reason: unknown) => setOrderError(reason instanceof Error ? reason.message : 'Не удалось открыть заказ'))
      .finally(() => setOrderLoading(false))
  }, [])

  useEffect(() => {
    if (presetKey !== 'orders' || state.selectedOrderId === null || selectedOrderId === state.selectedOrderId) return
    openOrderById(state.selectedOrderId)
  }, [openOrderById, presetKey, selectedOrderId, state.selectedOrderId])

  const openOrder = useCallback((row: InventoryGroupRow) => {
    const orderId = row.orderNumber === null ? Number.NaN : Number(row.orderNumber)
    if (Number.isSafeInteger(orderId) && orderId > 0) write({ selectedOrderId: orderId }, { push: true })
    openOrderById(orderId)
  }, [openOrderById, write])

  const closeOrder = useCallback(() => {
    setSelectedOrderId(null)
    setSelectedOrder(null)
    setOrderError(null)
    if (state.selectedOrderId !== null) write({ selectedOrderId: null })
  }, [state.selectedOrderId, write])

  return <div className="flex h-full min-h-0 flex-col bg-ivory">
    <div data-testid="warehouse-column" className="relative mx-auto flex min-h-0 w-full max-w-[1600px] flex-1 flex-col px-3 py-4 sm:px-5 md:px-7 md:py-6">
      <header data-testid="warehouse-header-row" className="flex flex-col gap-3">
        <div data-testid="warehouse-title-row" className="flex flex-wrap items-end gap-x-4 gap-y-2">
          <div className="shrink-0">
            <h1 className="text-[28px] font-semibold text-ink">{reconciliationMode ? 'Сверка источников' : 'Склад'}</h1>
            <p className="mt-1 text-xs text-ink-muted">{showResultCountLabel && error === null ? 'Найдено: ' : ''}{totalDescription}{state.lifecycle !== 'all' ? ` · ${lifecycleLabels[state.lifecycle]}` : ''}</p>
          </div>
          {/* The way back out of a source-record URL, which nothing on this page
              links to any more. */}
          {reconciliationMode ? <Link to="/inventory" className="btn btn-ghost text-xs">Изделия</Link> : null}
          {!reconciliationMode ? <PresetSwitcher value={presetKey} onChange={changePreset} /> : null}
          {headerButtons.length > 0 ? <div className="ml-auto flex flex-wrap items-end gap-2">
            {headerButtons.map((button) => (
              <button key={button} type="button" className="btn btn-ghost text-xs" title={HEADER_BUTTON_HINTS[button]} aria-describedby={`warehouse-header-hint-${button}`} disabled={button === 'ymarketCollect' && collecting} onClick={() => pressHeaderButton(button)}>
                {button === 'ymarketCollect' && collecting ? 'Запускаем сбор…' : HEADER_BUTTON_LABELS[button]}
              </button>
            ))}
            {headerButtons.map((button) => <span key={`hint-${button}`} id={`warehouse-header-hint-${button}`} className="sr-only">{HEADER_BUTTON_HINTS[button]}</span>)}
          </div> : null}
          {collectError !== null ? <p role="alert" className="basis-full text-right text-xs text-st-unpaid">{collectError}</p> : null}
        </div>
        {!reconciliationMode ? <WarehouseToolbar className="w-full" loading={loading} search={viewFilters.search ?? ''} grouping={state.grouping} withPhotos={state.withPhotos} withPhotosCount={inventory?.withPhotosPopulation ?? null} modelLevelAvailable={groups?.modelLevel.available ?? false} filterCount={filterChipCount} filtersOpen={filtersOpen} filterTriggerRef={filterTriggerRef} onSearch={(search) => write({ filters: { ...viewFilters, search: search || undefined }, page: 1 })} onGrouping={(grouping) => setSearchParams(serializeWarehouseUrl(changeGrouping(viewState, grouping), presetDefaults), { replace: true })} onWithPhotos={(withPhotos) => write({ withPhotos, filters: withPhotos ? { ...viewFilters, withoutPhotos: undefined } : viewFilters, page: 1 })} onFiltersOpen={() => setFiltersOpen((value) => !value)} visibleColumns={state.grouping === 'none' ? visibleColumns : undefined} onVisibleColumnsChange={state.grouping === 'none' ? (columns) => { void saved.save(presetKey, { columns, sort: inventorySortOf(viewSort) ?? builtinPreset.sort }) } : undefined} onVisibleColumnsReset={state.grouping === 'none' ? () => { void saved.reset(presetKey) } : undefined} columnsSaving={saved.saving} view={presetKey} /> : null}
      </header>

      {!reconciliationMode && (inventory?.stockMismatchCount ?? 0) > 0 ? <div data-testid="warehouse-stock-mismatch-signal" className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-st-unpaid/30 bg-st-unpaid/5 px-3 py-2 text-xs">
        <p className="font-medium text-ink">Найдены несовпадения остатков: {(inventory?.stockMismatchCount ?? 0).toLocaleString('ru-RU')}</p>
        <button type="button" className="btn btn-ghost text-xs" onClick={openStockMismatches}>Показать несовпадения</button>
      </div> : null}

      {!reconciliationMode ? <div className="mt-3 flex flex-wrap items-center gap-2">
        <WarehouseFilterChips parkedNote={parkedNote} filters={viewFilters} lifecycle={state.lifecycle} onLifecycle={(lifecycle, filters) => write({ lifecycle, filters, page: 1 })} providerSlots={inventory?.providerSlots ?? null} availableFilters={inventory?.availableFilters ?? null} onChange={(filters) => write({ filters, page: 1 })} />
      </div> : null}
      <WarehouseFilters filters={viewFilters} availableFilters={inventory?.availableFilters ?? null} providerSlots={inventory?.providerSlots ?? null} grouping={state.grouping} lifecycle={state.lifecycle} open={filtersOpen} triggerRef={filterTriggerRef} onClose={() => setFiltersOpen(false)} onLifecycle={(lifecycle, filters) => write({ lifecycle, filters, page: 1 })} onChange={(filters) => write({ filters, page: 1 })} onTogglePreset={(presetId) => { const filters = togglePreset(viewFilters, presetId); write({ filters, withPhotos: filters.withoutPhotos ? false : state.withPhotos, page: 1 }) }} onReset={() => write({ filters: resetFilters(builtinPreset.filters, viewFilters), withPhotos: false, lifecycle: 'all', page: 1 })} readyCounts={inventory?.readyCounts ?? {}} view={presetKey} counts={panelCounts} resultLabel={loading ? null : `Найдено: ${totalDescription}`} />
      <div data-testid="warehouse-layout" className="mt-3 flex min-h-0 flex-1 flex-col gap-3">
        <div data-testid="warehouse-results-region" className="relative flex min-h-0 flex-1 flex-col">
          <div data-testid="warehouse-list-pane" className="min-h-0 overflow-y-auto" style={selectionBarHeight > 0 ? { paddingBottom: selectionBarHeight } : undefined}>
            {reconciliationMode ? <WarehouseLeafTable rows={populationRows} loading={loading} error={error} onRowActivate={openPopulationRow} selectedRowKey={selectedPopulationRowKey} sort={viewSort} onSortChange={(sort) => changeSort(sort.column, sort.direction)} reconciliationMode /> : state.grouping === 'none' ? <ProductTable rows={products} inventory={inventory} loading={loading} error={error} sort={viewSort} onSortChange={(sort) => changeSort(sort.column, sort.direction)} onActivate={openPreview} selectedVariantId={state.selectedVariantId} visibleColumns={visibleColumns} selection={selection} outlierIds={outlierIds} eligibility={rowEligibility} onSelectAll={selectAllEligible} /> : <WarehouseGroupedTable selection={state.grouping === 'order' ? undefined : selection} selectionTotal={inventory?.total ?? 0} onSelectAll={selectAllEligible} visibleColumns={visibleColumns} onOrderActivate={state.grouping === 'order' ? openOrder : undefined} onVariantActivate={(variantId) => { const variant = products.find((row) => row.productId === variantId); if (variant) openPreview(variant); else write({ selectedVariantId: variantId }, { push: true }) }} page={groups} filters={groupingFilters} loading={loading} error={error} onUnitActivate={(unit) => write({ selectedVariantId: unit.productId }, { push: true })} onPopulationRowActivate={openPopulationRow} selectedRowKey={selectedPopulationRowKey} sort={viewSort} onSortChange={(sort) => changeSort(sort.column, sort.direction)} />}
            {error === null && totalKnown ? <Pagination page={state.page} pageSize={state.pageSize} total={total} noun={totalNoun} onPageChange={(page) => write({ page })} onPageSizeChange={(pageSize) => write({ pageSize, page: 1 })} /> : null}
          </div>
          {selected ? <DetailOverlay modal={false} width={880} label={`Быстрый просмотр: ${selected.title ?? 'товар'}`} onClose={closePreview}>
            <ProductQuickPreview variant={{ ...selected, ymarketEconomics: selectedEconomics }} providerSlots={inventory?.providerSlots ?? null} onClose={closePreview} onVariantPick={(variantId) => write({ selectedVariantId: variantId })} marketSection={<MarketEconomicsSection productId={selected.productId} economics={selectedEconomics} />} />
          </DetailOverlay> : null}
        </div>
      </div>
    </div>
    <CardSettingsDialog open={cardSettingsSection !== null} focusSection={cardSettingsSection ?? 'cards'} sampleProductId={inStockSampleProductId} onClose={() => setCardSettingsSection(null)} />
    {!reconciliationMode ? <WarehouseSelectionBar selection={selection} actions={selectionActions} rows={selectedRows} onActionDone={() => setActionRefreshToken((current) => current + 1)} onStatsChange={setSelectionStats} onHeightChange={setSelectionBarHeight} /> : null}
    {selectedOrderId !== null ? <OrderDetailDrawer orderId={selectedOrderId} onClose={closeOrder}>
      {orderLoading ? <p role="status" className="text-sm text-ink-muted">Загружаем заказ…</p> : orderError ? <p role="alert" className="text-sm text-st-unpaid">{orderError}</p> : selectedOrder ? <OrderDetailContent order={selectedOrder} messageStates={selectedOrder.notificationState?.messageStates ?? []} /> : null}
    </OrderDetailDrawer> : null}
  </div>
}

export default function InventoryPage({ refreshToken = 0 }: { refreshToken?: number } = {}) {
  return <WarehousePage refreshToken={refreshToken} />
}
